// app/javascript/projects/components/ProjectsDisplay.jsx

import React, { useState } from 'react';

import SideBarButton from './SideBarButton'
import { useProjectModal } from '../../contexts/ProjectModalContext'
import SupportDropdownButton from './SupportDropdownButton'
import ColorConstants from 'constants/ColorConstants'
import { useHistory, useLocation } from 'react-router-dom';
import { Scrollbars } from 'react-custom-scrollbars';

import ProjectPickerModal from './ProjectPickerModal'
import FileLoadingModal from './FileLoadingModal'
import AccountFooter from './AccountFooter'
import TranscriptLinks from 'left_side_bar/LeftSideTranscripts';
import LeftSideProject from 'left_side_bar/LeftSideProject';
import LeftSideSurveyList from 'left_side_bar/LeftSideSurveyList';

import { LEFT_PADDING, headerStyle, selectedStyle, footerStyle } from 'left_side_bar/LeftSideBarStyles';
import { useAllProjectTranscripts } from '../../hooks/transcripts/queries';

const LeftSideBar = (props) => {
  const history = useHistory();
  const location = useLocation();
  const pathname = location.pathname;
  const { showProjectModal } = useProjectModal();
  const [transcriptsHovered, setTranscriptsHovered] = useState(false);
  const [addTranscriptHovered, setAddTranscriptHovered] = useState(false);

  // The path after `/projects/:id` (e.g. '/reports/codes-doc'). Matching the nav
  // link against this as a full segment prevents '/codes' from matching
  // '/reports/codes-doc' (which a loose substring check would).
  const projectSubPath = pathname.replace(/^\/projects\/[^/]+/, '');

  // Check if sidebar link path matches current pathname
  const getHeaderStyle = (linkPath) => {
    if (!linkPath) return headerStyle;
    const isActive =
      projectSubPath === linkPath || projectSubPath.startsWith(`${linkPath}/`);
    return isActive ? selectedStyle : headerStyle;
  };

  const onLinkClick = (to) => {
    history.push(to);
  };

  const isOnUserPage = pathname.includes('/users');
  const projectName = isOnUserPage ? null : props.project?.name;

  // Until the list has actually loaded, treat the project as having
  // transcripts so an early click keeps the old dashboard target.
  const { data: transcripts = [], isSuccess: transcriptsLoaded } =
    useAllProjectTranscripts(props.projectID, { enabled: !isOnUserPage && !!props.projectID });
  const projectHasNoTranscripts = transcriptsLoaded && transcripts.length === 0;

  return (
    <div style={{
      background: ColorConstants.DARK_GREY, 
      color: ColorConstants.LIGHT_GREY, 
      height: "100%", 
      maxWidth: "100%",
      display: "flex",
      flexDirection: "column"
    }}>
      <ProjectPickerModal />
      <FileLoadingModal/>

      <Scrollbars autoHide style={{flexGrow: 1, flexShrink: 1, flexBasis: 0, overflow: 'hidden', maxWidth: "100%", marginRight: "0px"}}>
        <div style={{display: "flex", flexDirection: "column", minHeight: "100%"}}>
          <div style={{flex: 1}}>
            <LeftSideProject
              hasEditPermission={props.hasEditPermission}
              isOnUserPage={isOnUserPage}
              project={props.project}
              projectName={projectName}
              leftPadding={LEFT_PADDING}
            />

            <div style={{clear:"both"}}></div>

            {(props.hasEditPermission || isOnUserPage) &&
              <SideBarButton
                to={`/projects/` + props.projectID + `/search`}
                isOnUserPage={isOnUserPage}
                linkStyle={getHeaderStyle('/search')}
              >
                Search
              </SideBarButton>
            }

            {(props.hasEditPermission || isOnUserPage) && !!process.env['AI_FEATURE_FLAG'] &&
              <SideBarButton
                to={`/projects/` + props.projectID + `/chat`}
                isOnUserPage={isOnUserPage}
                linkStyle={getHeaderStyle('/chat')}
              >
                AI Chat +
              </SideBarButton>
            }


            <SideBarButton
              className="codesTab"
              to={`/projects/` + props.projectID + `/codes`}
              isOnUserPage={isOnUserPage}
              linkStyle={getHeaderStyle('/codes')}
            >
              Codes
            </SideBarButton>

            {(isOnUserPage || props.hasEditPermission) &&
              <SideBarButton
                to={`/projects/` + props.projectID + `/analysis`}
                isOnUserPage={isOnUserPage}
                linkStyle={getHeaderStyle('/analysis')}
              >
                Snippets
              </SideBarButton>
            }

            {(isOnUserPage || props.hasEditPermission) &&
              <SideBarButton
                className="reportsTab"
                to={`/projects/` + props.projectID + `/reports`}
                isOnUserPage={isOnUserPage}
                linkStyle={getHeaderStyle('/reports')}
              >
                Reports
              </SideBarButton>
            }

            {/* The dashboard is view-only friendly; only the upload + needs edit rights.
                The label and + are split so each gets its own hover highlight and
                an easy click target; on the users page both open the project picker. */}
            <div style={{ ...headerStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '12px' }}>
              <div
                onClick={() => {
                  if (isOnUserPage) return showProjectModal();
                  // An empty project has nothing to show on the dashboard —
                  // land on the add-transcript onboarding screen instead.
                  const subPath = projectHasNoTranscripts ? '/transcripts/new' : '/transcripts';
                  history.push(`/projects/${props.projectID}${subPath}`);
                }}
                onMouseEnter={() => setTranscriptsHovered(true)}
                onMouseLeave={() => setTranscriptsHovered(false)}
                style={{
                  cursor: 'pointer',
                  flex: 1,
                  // exact match only — '/transcripts/new' must not light up the label
                  color: projectSubPath === '/transcripts' ? 'white' : undefined,
                  borderRadius: '4px',
                  background: transcriptsHovered ? 'rgba(255,255,255,0.08)' : 'transparent',
                  padding: '2px 4px',
                  margin: '-2px -4px',
                }}
              >
                Transcripts
              </div>
              {(isOnUserPage || props.hasEditPermission) &&
                <button
                  type="button"
                  aria-label="add transcript"
                  onClick={() => isOnUserPage ? showProjectModal() : history.push(`/projects/${props.projectID}/transcripts/new`)}
                  onMouseEnter={() => setAddTranscriptHovered(true)}
                  onMouseLeave={() => setAddTranscriptHovered(false)}
                  style={{
                    cursor: 'pointer',
                    // strip native button chrome so it looks identical to the
                    // old div; keep the default focus outline for keyboard users
                    appearance: 'none',
                    border: 'none',
                    padding: 0,
                    fontFamily: 'inherit',
                    // flex-center the glyph in a fixed box so the + is truly
                    // centered (lineHeight + padding alone left it sitting high)
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '26px',
                    height: '24px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    lineHeight: 1,
                    // 'inherit' (not undefined) so an unselected + keeps the
                    // sidebar's text color instead of the browser button default
                    color: projectSubPath === '/transcripts/new' ? 'white' : 'inherit',
                    borderRadius: '4px',
                    background: addTranscriptHovered ? 'rgba(255,255,255,0.12)' : 'transparent',
                  }}
                >
                  +
                </button>
              }
            </div>

            <TranscriptLinks
              projectID={props.projectID}
              project={props.project}
              isOnUserPage={isOnUserPage}
              transcriptID={props.transcriptID}
              onLinkClick={onLinkClick}
            />

            {(isOnUserPage || props.hasEditPermission) &&
              <SideBarButton
                to={`/projects/` + props.projectID + `/surveys/new`}
                isOnUserPage={isOnUserPage}
                linkStyle={getHeaderStyle('/surveys/new')}
              >
                Surveys +
              </SideBarButton>
            }

            <LeftSideSurveyList
              projectId={props.projectID}
              isOnUserPage={isOnUserPage}
              survey_id={props.survey_id}
              onLinkClick={onLinkClick}
            />
          </div>

          <SupportDropdownButton
            linkStyle={getHeaderStyle()}
            isOnUserPage={isOnUserPage}
          />
        </div>
      </Scrollbars>

      <div style={{flexShrink: 0, flexGrow: 0}}>
        <AccountFooter name={props.user?.name} initials={props.user?.initials}/>
      </div>
    </div>
  );
};

export default LeftSideBar;
